import { useEffect, useRef, useState } from 'react';
import { UserAccount } from 'api/account/account.types';
import { streamChatAuth } from 'api/chat/streamChatAuth';
import { StreamClient } from 'helpers/streamChat';
import { DefaultGenerics, StreamChat } from 'stream-chat';

/**
 * Provides the StreamChat client. Also guarantees that the currentAccount is identified
 */
const useStreamClient = (
  account: UserAccount | undefined,
): {
  clientId: string | null;
  chatClient: StreamChat<DefaultGenerics> | null;
} => {
  const [streamToken, setStreamToken] = useState<string>();
  const [chatClient, setChatClient] = useState<typeof StreamClient>(
    () => StreamClient,
  );
  const [clientId, setClientId] = useState<string | null>(null);

  if (!StreamClient) return { clientId: null, chatClient: null };

  const chatInitialized = useRef(false);

  // pre-initialize step: get user token
  useEffect(() => {
    if (!account) return;

    streamChatAuth().then(setStreamToken);
  }, [account]);

  // Ensure the StreamClient is initialized
  useEffect(() => {
    if (!account) return;
    if (!streamToken) return;

    const connectUser = async (id: string) => {
      return StreamClient.connectUser(
        {
          id,
        },
        streamToken,
      ).then(() => {
        setClientId(StreamClient.clientID ?? null);
      });
    };

    const handleConnectionChange = ({ online = false }) => {
      // Connection lost
      if (!online) return;

      // Connection on
      if (!account.streamChatId) return;

      if (!StreamClient.user) {
        connectUser(account.streamChatId).then(() => {
          setChatClient(StreamClient);
        });
      }
    };

    if (chatInitialized.current) return;

    chatInitialized.current = true;
    connectUser(account.streamChatId).then(() => {
      setChatClient(StreamClient);
    });

    StreamClient.on('connection.changed', handleConnectionChange);
  }, [account, streamToken]);

  return { clientId, chatClient };
};

export default useStreamClient;
